import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    ActivityIndicator,
    Modal,
    Pressable,
    RefreshControl,
    ScrollView,
    StyleSheet,
    Text,
    View,
} from "react-native";
import LinearGradient from "react-native-linear-gradient";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
    endOfMonth,
    endOfQuarter,
    endOfWeek,
    endOfYear,
    format,
    parse,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    startOfYear,
} from "date-fns";
import { RouteProp, useFocusEffect, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { SringarService } from "../../../services/SringarService";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";

type HistoryRowApi = {
    data: {
        sringarId: string;
        date: string;
        isGuruPuja: boolean;
        marks: number;
        isEditable : boolean;
    };
    date: string;
};

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type SringarHistoryRouteProp = RouteProp<AuthStackParamList, "SringarHistory">;

export function SringarHistoryScreen() {
    const route = useRoute<SringarHistoryRouteProp>();
    const { user } = useAuth();

    const today = useMemo(() => new Date(), []);
    const initialRange = resolveHistoryRange(route.params, today);

    const [filterType, setFilterType] = useState<FilterType>(
        initialRange.hasNavigationDates ? "Custom" : "Monthly"
    );

    const effectiveUserId = useMemo(() => {
        return initialRange.devoteeId || user?.userId || "";
    }, [initialRange.devoteeId, user?.userId]);

    const [fromDate, setFromDate] = useState(initialRange.fromDate);
    const [toDate, setToDate] = useState(initialRange.toDate);

    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [rows, setRows] = useState<HistoryRowApi[]>([]);

    const [editOpen, setEditOpen] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [editDateIso, setEditDateIso] = useState("");
    const [editDateLabel, setEditDateLabel] = useState("");
    const [editGuru, setEditGuru] = useState(true);
    const [summary, setSummary] = useState({ isSelf: true });
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

    const filters: FilterType[] = ["Weekly", "Monthly", "Quarterly", "Yearly"];

    const openDatePicker = (type: "from" | "to") => {
        const current = type === "from" ? fromDate : toDate;

        DateTimePickerAndroid.open({
            value: new Date(current),
            mode: "date",
            maximumDate: new Date(),
            onChange: (e, d) => {
                if (e.type === "set" && d) {
                    const v = format(d, "yyyy-MM-dd");
                    type === "from" ? setFromDate(v) : setToDate(v);
                    setFilterType("Custom");
                }
            },
        });
    };

    const applyFilter = (type: FilterType) => {
        setFilterType(type);

        const now = new Date();
        let s = startOfMonth(now);
        let e = endOfMonth(now);

        if (type === "Weekly") {
            s = startOfWeek(now);
            e = endOfWeek(now);
        } else if (type === "Quarterly") {
            s = startOfQuarter(now);
            e = endOfQuarter(now);
        } else if (type === "Yearly") {
            s = startOfYear(now);
            e = endOfYear(now);
        }

        setFromDate(format(s, "yyyy-MM-dd"));
        setToDate(format(e, "yyyy-MM-dd"));
    };

    const loadData = useCallback(async () => {
        if (!user?.userId) return;

        try {
            if (!effectiveUserId) return;
            setLoading(true);

            const res = await SringarService.history({
                userId: effectiveUserId,
                fromDate,
                toDate,
            });

            if (res?.status !== 200) {
                Flash.error(res?.message || "Failed to load history");
                return;
            }

            setRows(res.data.records || []);
            setSummary(res.data.response);
        } catch (e: any) {
            Flash.error(e?.message || "History load error");
        } finally {
            setLoading(false);
        }
    }, [user?.userId, fromDate, toDate]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useFocusEffect(
        useCallback(() => {
            loadData();
        }, [loadData])
    );

    const onRefresh = async () => {
        setRefreshing(true);
        try {
            await loadData();
        } finally {
            setRefreshing(false);
        }
    };

    const displayDate = (ddmmyyyy?: string) => {
        const d = parse(ddmmyyyy!, "dd-MM-yyyy", new Date());
        return {
            top: format(d, "dd MMM yyyy"),
            day: format(d, "EEEE"),
            long: format(d, "dd MMMM yyyy"),
            iso: format(d, "yyyy-MM-dd"),
        };
    };

    const openEdit = async (id: string, date: string) => {
        const d = displayDate(date);

        setEditId(id);
        setEditDateIso(d.iso);
        setEditDateLabel(d.long);

        const res = await SringarService.get(id);
        const one = res?.data?.[0];

        setEditGuru(one?.isGuruPuja ?? false);

        setEditOpen(true);
    };

    const onUpdate = async () => {
        if (!editId || !user?.userId) return;

        try {
            setEditLoading(true);

            const payload = {
                sringarId: editId,
                userId: user.userId,
                date: editDateIso,
                isGuruPuja: editGuru,
                marks: editGuru ? 3 : 0,
            };

            const res = await SringarService.update(payload);

            if (res?.status !== 200) {
                Flash.error(res?.message || "Update failed");
                return;
            }

            Flash.success("Success!", "Sringar & Guru Puja Updated Successfully!");
            setEditOpen(false);
            loadData();
        } catch (e: any) {
            Flash.error(e?.message || "Update failed");
        } finally {
            setEditLoading(false);
        }
    };

    const onDelete = (id: string) => {
        setPendingDeleteId(id);
    };

    return (
        <DivineBackground overlayOpacity={0.92}>
            <View style={{ flex: 1 }}>
                <Header title="History & Analytics" showBack showHome />

                <ScrollView
                    contentContainerStyle={styles.container}
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing}
                            onRefresh={onRefresh}
                            colors={["#F97316"]}
                            tintColor="#F97316"
                        />
                    }
                >
                    {/* FILTER CARD */}
                    <View style={styles.filterCard}>
                        <View style={styles.filterHeader}>
                            <Text style={styles.filterIcon}>☀</Text>
                            <Text style={styles.filterTitle}>Filter History</Text>
                        </View>

                        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pillsRow}>
                            {filters.map((f) => {
                                const active = filterType === f;

                                return (
                                    <Pressable
                                        key={f}
                                        onPress={() => applyFilter(f)}
                                        style={[styles.pill, active ? styles.pillActive : styles.pillInactive]}
                                    >
                                        <Text style={active ? styles.pillTextActive : styles.pillTextInactive}>{f}</Text>
                                    </Pressable>
                                );
                            })}
                        </ScrollView>

                        <View style={styles.rangeRow}>
                            <View style={{ flex: 1 }}>
                                <Text style={styles.rangeLabel}>From Date</Text>

                                <Pressable onPress={() => openDatePicker("from")} style={styles.rangeInput}>
                                    <Text>📅</Text>
                                    <Text style={styles.rangeText}>
                                        {format(new Date(fromDate), "dd-MM-yyyy")}
                                    </Text>
                                </Pressable>
                            </View>

                            <View style={{ width: 12 }} />

                            <View style={{ flex: 1 }}>
                                <Text style={styles.rangeLabel}>To Date</Text>

                                <Pressable onPress={() => openDatePicker("to")} style={styles.rangeInput}>
                                    <Text>📅</Text>
                                    <Text style={styles.rangeText}>
                                        {format(new Date(toDate), "dd-MM-yyyy")}
                                    </Text>
                                </Pressable>
                            </View>
                        </View>
                    </View>

                    {/* LIST */}
                    {loading ? (
                        <ActivityIndicator />
                    ) : rows.length === 0 ? (
                        <View style={styles.emptyBox}>
                            <Text style={styles.emptyText}>No records found for this period.</Text>
                        </View>
                    ) : (
                        rows.map((r) => {
                            const d = displayDate(r.date);

                            return (
                                <View key={r.data.sringarId} style={styles.itemRow}>
                                    <View>
                                        <Text style={styles.itemDate}>{d.top}</Text>
                                        <Text style={styles.itemDay}>{d.day}</Text>
                                    </View>

                                    <View style={styles.itemRight}>
                                        <View style={{ alignItems: "center" }}>
                                            <Text style={r.data.isGuruPuja ? styles.check : styles.cross}>
                                                {r.data.isGuruPuja ? "✓" : "✕"}
                                            </Text>
                                            <Text style={styles.smallMuted}>Guru Pooja</Text>
                                        </View>

                                        <View style={{ alignItems: "center" }}>
                                            <Text style={styles.marks}>{r.data.marks}</Text>
                                            <Text style={styles.smallMuted}>Marks</Text>
                                        </View>

                                        {summary.isSelf && (
                                            <View style={styles.actions}>
                                                {r.data.isEditable ? (
                                                    <Pressable onPress={() => openEdit(r.data.sringarId, r.date)} style={styles.iconBtn}>
                                                        <Text style={styles.iconBtnText}>✏️</Text>
                                                    </Pressable>
                                                ) : (
                                                    <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                                                )}

                                                <Pressable onPress={() => onDelete(r.data.sringarId)} style={[styles.iconBtn, styles.iconBtnDanger]}>
                                                    <Text style={[styles.iconBtnText, { color: "#DC2626" }]}>🗑️</Text>
                                                </Pressable>
                                            </View>
                                        )}
                                    </View>
                                </View>
                            );
                        })
                    )}
                </ScrollView>

                {/* EDIT MODAL */}
                <Modal visible={editOpen} transparent animationType="fade">
                    <View style={styles.modalOverlay}>
                        <View style={styles.modalCard}>

                            <View style={styles.modalHeader}>
                                <Text style={styles.modalTitle}>Edit Entry</Text>

                                <Pressable
                                    onPress={() => setEditOpen(false)}
                                    style={styles.modalClose}
                                >
                                    <Text style={{ fontSize: 16, fontWeight: "900" }}>×</Text>
                                </Pressable>
                            </View>

                            <Text style={styles.modalDate}>Date: {editDateLabel}</Text>

                            {/* YES OPTION */}
                            <Pressable
                                onPress={() => setEditGuru(true)}
                                style={[
                                    styles.optionCard,
                                    editGuru && styles.optionActive,
                                ]}
                            >
                                <Text style={styles.optionText}>Guru Pooja Done (3)</Text>

                                {editGuru && (
                                    <Text style={styles.optionCheck}>✓</Text>
                                )}
                            </Pressable>

                            {/* NO OPTION */}
                            <Pressable
                                onPress={() => setEditGuru(false)}
                                style={[
                                    styles.optionCard,
                                    !editGuru && styles.optionActive,
                                ]}
                            >
                                <Text style={styles.optionText}>Not Done (0)</Text>

                                {!editGuru && (
                                    <Text style={styles.optionCheck}>✓</Text>
                                )}
                            </Pressable>

                            <View style={styles.modalButtons}>

                                <Pressable
                                    onPress={() => setEditOpen(false)}
                                    disabled={editLoading}
                                    style={({ pressed }) => [
                                        styles.modalCancel,
                                        pressed ? { opacity: 0.95 } : null,
                                        editLoading ? { opacity: 0.6 } : null,
                                    ]}
                                >
                                    <Text style={styles.modalCancelText}>Cancel</Text>
                                </Pressable>

                                <Pressable
                                    disabled={editLoading}
                                    onPress={onUpdate}
                                    style={styles.modalUpdatePressable}
                                >
                                    <LinearGradient
                                        colors={["#EC4899", "#BE185D"]}
                                        start={{ x: 0, y: 0.5 }}
                                        end={{ x: 1, y: 0.5 }}
                                        style={[styles.modalUpdate, editLoading ? { opacity: 0.75 } : null]}
                                    >
                                        <Text style={styles.modalUpdateText}>
                                            {editLoading ? "Updating..." : "Update"}
                                        </Text>
                                    </LinearGradient>
                                </Pressable>

                            </View>

                        </View>
                    </View>
                </Modal>
                <ConfirmDialog
                    visible={!!pendingDeleteId}
                    title="Delete Entry"
                    message="Are you sure?"
                    confirmText="Delete"
                    destructive
                    onCancel={() => setPendingDeleteId(null)}
                    onConfirm={async () => {
                        if (!pendingDeleteId) return;
                        const res = await SringarService.delete(pendingDeleteId);

                        if (res?.status !== 200) {
                            Flash.error("Delete failed");
                            return;
                        }

                        Flash.success("Success!", "Sringar & Guru Puja Deleted Successfully!");
                        setPendingDeleteId(null);
                        loadData();
                    }}
                />
            </View>
        </DivineBackground>
    );
}

const styles = StyleSheet.create({
    container: { padding: 14, gap: 12 },

    filterCard: {
        backgroundColor: "rgba(255,255,255,0.9)",
        borderRadius: 16,
        padding: 14,
    },

    filterHeader: { flexDirection: "row", gap: 8 },

    filterIcon: { fontSize: 16 },

    filterTitle: { fontWeight: "900", fontSize: 14 },

    pillsRow: { gap: 10, paddingVertical: 10 },

    pill: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999 },

    pillActive: { backgroundColor: "#EC4899" },

    pillInactive: { backgroundColor: "rgba(236,72,153,0.1)" },

    pillTextActive: { color: "#fff", fontWeight: "900" },

    pillTextInactive: { color: "#BE185D", fontWeight: "900" },

    rangeRow: { flexDirection: "row" },

    rangeLabel: { fontSize: 11, marginBottom: 6 },

    rangeInput: {
        flexDirection: "row",
        gap: 6,
        padding: 10,
        borderRadius: 10,
        backgroundColor: "#eee",
    },

    rangeText: { fontWeight: "900" },

    emptyBox: {
        backgroundColor: "rgba(255,255,255,0.55)",
        borderRadius: 16,
        paddingVertical: 28,
        alignItems: "center",
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.06)",
    },
    emptyText: { marginTop: 8, color: "rgba(74,35,17,0.55)", fontWeight: "800" },

    itemRow: {
        backgroundColor: "#fff",
        padding: 14,
        borderRadius: 16,
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    itemDate: { fontWeight: "900" },

    itemDay: { fontSize: 12, opacity: 0.6 },

    itemRight: { flexDirection: "row", gap: 12, alignItems: "center" },

    smallMuted: { fontSize: 10, opacity: 0.5 },

    check: { color: "green", fontWeight: "900" },

    cross: { color: "red", fontWeight: "900" },

    marks: { fontWeight: "900", color: "#BE185D" },

    actions: { flexDirection: "row", gap: 10 },

    modalOverlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.45)",
        justifyContent: "center",
        alignItems: "center",
    },

    modalCard: {
        backgroundColor: "#fff",
        padding: 20,
        borderRadius: 16,
        width: "85%",
    },

    modalTitle: { fontSize: 16, fontWeight: "900" },

    modalDate: { marginTop: 8 },

    modalButtons: {
        flexDirection: "row",
        gap: 12,
        marginTop: 18,
    },

    iconBtn: {
        width: 36,
        height: 36,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(37,99,235,0.10)",
    },
    iconBtnDanger: { backgroundColor: "rgba(220,38,38,0.10)" },
    iconBtnPlaceholder: { opacity: 0 },
    iconBtnText: { fontSize: 14, fontWeight: "900", color: "#1D4ED8" },

    cancel: {
        flex: 1,
        height: 44,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(236,72,153,0.30)",
        backgroundColor: "#fff",
        alignItems: "center",
        justifyContent: "center",
    },

    update: {
        flex: 1,
        height: 44,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOpacity: 0.14,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 4,
    },

    modalHeader: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    },

    modalClose: {
        width: 30,
        height: 30,
        borderRadius: 999,
        backgroundColor: "rgba(0,0,0,0.06)",
        alignItems: "center",
        justifyContent: "center",
    },

    optionCard: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        padding: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(0,0,0,0.08)",
        marginTop: 12,
        backgroundColor: "#fff",
    },

    optionActive: {
        backgroundColor: "rgba(236,72,153,0.12)",
        borderColor: "#EC4899",
    },

    optionText: {
        fontWeight: "800",
        fontSize: 13,
    },

    optionCheck: {
        fontWeight: "900",
        color: "#EC4899",
        fontSize: 16,
    },

    updateText: {
        color: "#fff",
        fontWeight: "900",
    },

    modalCancel: {
        flex: 1,
        height: 44,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: "rgba(236,72,153,0.30)",
        backgroundColor: "#fff",
        alignItems: "center",
        justifyContent: "center",
    },

    modalCancelText: {
        fontSize: 13,
        fontWeight: "900",
        color: "#BE185D",
    },

    modalUpdatePressable: {
        flex: 1,
    },

    modalUpdate: {
        flex: 1,
        height: 44,
        borderRadius: 12,
        alignItems: "center",
        justifyContent: "center",
        shadowColor: "#000",
        shadowOpacity: 0.14,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 6 },
        elevation: 4,
    },

    modalUpdateText: {
        fontSize: 13,
        fontWeight: "900",
        color: "#fff",
    },
});
